import Phaser from "phaser";
import { OBLIQUE_TILE } from "../projection/ObliqueProjection";

/**
 * Procedural placeholder tileset for oblique zones.
 * Same dimensions as the top-down tileset (32×32), different palette + a wall
 * tile with a front-face shading hint so the perspective reads slightly
 * different from pure top-down even before real ¾-view art lands.
 *
 * Frames (TMJ tile IDs are 1-indexed):
 *   0 (TMJ 1) → grass (warmer green)
 *   1 (TMJ 2) → cobble (warmer grey)
 *   2 (TMJ 3) → planked path (wooden boards)
 *   3 (TMJ 4) → water (same as top-down)
 *   4 (TMJ 5) → stone wall with front-face shading
 */
export const OBLIQUE_TILESET_KEY = "oblique-base";

const FRAMES = 5;

export function ensureObliqueTextures(scene: Phaser.Scene): void {
  if (scene.textures.exists(OBLIQUE_TILESET_KEY)) return;

  const W = OBLIQUE_TILE * FRAMES;
  const H = OBLIQUE_TILE;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);

  drawGrassWarm(g, 0);
  drawCobbleWarm(g, OBLIQUE_TILE);
  drawPlankedPath(g, OBLIQUE_TILE * 2);
  drawWater(g, OBLIQUE_TILE * 3);
  drawWallWithFrontFace(g, OBLIQUE_TILE * 4);

  g.generateTexture(OBLIQUE_TILESET_KEY, W, H);
  g.destroy();

  const tex = scene.textures.get(OBLIQUE_TILESET_KEY);
  for (let i = 0; i < FRAMES; i++) {
    tex.add(i, 0, i * OBLIQUE_TILE, 0, OBLIQUE_TILE, OBLIQUE_TILE);
  }
}

function drawGrassWarm(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x5a8a48, 1);
  g.fillRect(ox, 0, OBLIQUE_TILE, OBLIQUE_TILE);
  g.fillStyle(0x6a9a58, 1);
  for (let i = 0; i < 10; i++) {
    const x = ox + ((i * 7 + 3) % OBLIQUE_TILE);
    const y = (i * 11 + 5) % OBLIQUE_TILE;
    g.fillRect(x, y, 2, 2);
  }
  g.lineStyle(1, 0x3a6a28, 0.4);
  g.strokeRect(ox + 0.5, 0.5, OBLIQUE_TILE - 1, OBLIQUE_TILE - 1);
}

function drawCobbleWarm(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x9a8a72, 1);
  g.fillRect(ox, 0, OBLIQUE_TILE, OBLIQUE_TILE);
  g.lineStyle(1, 0x6a5a42, 0.7);
  for (let row = 0; row < 4; row++) {
    const offsetX = (row % 2) * 4;
    for (let col = 0; col < 4; col++) {
      const x = ox + col * 8 + offsetX - (offsetX > 0 && col === 3 ? 8 : 0);
      const y = row * 8;
      g.strokeRect(x + 0.5, y + 0.5, 7, 7);
    }
  }
}

function drawPlankedPath(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x9a7a4a, 1);
  g.fillRect(ox, 0, OBLIQUE_TILE, OBLIQUE_TILE);
  // Horizontal plank seams
  g.lineStyle(1, 0x5a3a1a, 0.8);
  for (let i = 1; i < 4; i++) {
    const y = i * 8;
    g.lineBetween(ox + 1, y, ox + OBLIQUE_TILE - 1, y);
  }
  // Plank-end nails
  g.fillStyle(0x3a2a1a, 0.8);
  for (let i = 0; i < 4; i++) {
    g.fillCircle(ox + 3, i * 8 + 4, 1);
    g.fillCircle(ox + OBLIQUE_TILE - 3, i * 8 + 4, 1);
  }
}

function drawWater(g: Phaser.GameObjects.Graphics, ox: number): void {
  g.fillStyle(0x1a5a8a, 1);
  g.fillRect(ox, 0, OBLIQUE_TILE, OBLIQUE_TILE);
  g.lineStyle(1, 0x3a8aba, 0.5);
  for (let i = 0; i < 4; i++) {
    const y = 4 + i * 8;
    g.beginPath();
    g.moveTo(ox + 2, y);
    g.lineTo(ox + 10, y + 2);
    g.lineTo(ox + 18, y);
    g.lineTo(ox + 26, y + 2);
    g.strokePath();
  }
}

/**
 * Wall with a hinted front-face: lighter top edge, darker shaded lower half,
 * suggesting depth-from-above. Stays within 32×32 — true tall-prop rendering
 * is a future renderer task.
 */
function drawWallWithFrontFace(g: Phaser.GameObjects.Graphics, ox: number): void {
  // Top "cap" (lit)
  g.fillStyle(0x8a7a6a, 1);
  g.fillRect(ox, 0, OBLIQUE_TILE, 8);
  // Front face (shaded)
  g.fillStyle(0x4a3a2a, 1);
  g.fillRect(ox, 8, OBLIQUE_TILE, OBLIQUE_TILE - 8);
  // Stone block seams on the front face
  g.lineStyle(1, 0x2a1a10, 0.9);
  g.lineBetween(ox + 0, 16, ox + OBLIQUE_TILE, 16);
  g.lineBetween(ox + 0, 24, ox + OBLIQUE_TILE, 24);
  g.lineBetween(ox + 10, 8, ox + 10, 16);
  g.lineBetween(ox + 22, 8, ox + 22, 16);
  g.lineBetween(ox + 6, 16, ox + 6, 24);
  g.lineBetween(ox + 18, 16, ox + 18, 24);
  g.lineBetween(ox + 14, 24, ox + 14, OBLIQUE_TILE);
  g.lineBetween(ox + 26, 24, ox + 26, OBLIQUE_TILE);
  // Outline
  g.lineStyle(1, 0x1a0a04, 1);
  g.strokeRect(ox + 0.5, 0.5, OBLIQUE_TILE - 1, OBLIQUE_TILE - 1);
}
